/**
 * Record workspace — the standard shape for every record list + detail surface.
 *
 * Card/table toggle, split workspace, condensed list, detail shell, tabs. These
 * were built for Cascade's Leads and lived in that app, which is why each new
 * record type in another portal drifted from the intended standard: reaching it
 * required copy-paste. They now sit beside MasterDetail so One Up Admin, Cascade,
 * and the Client portal can all compose the same primitives.
 */
import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, ChevronLeft, ChevronRight, LayoutGrid, Rows3, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { useMediaQuery } from '../hooks/use-media-query';
import { MasterDetail } from './master-detail';
import {
  RecordTypeStrip,
  RegisteredRecordTypeStrip,
  type RecordTypeId,
  type RegisteredRecordTypeDefinition,
} from './record-type-strip';
import { cn } from '../lib/utils';

export type CollectionView = 'list' | 'cards';

/**
 * Which shape a record collection is drawn in, remembered per surface.
 *
 * Two behaviours worth stating, because both were wrong before:
 *
 * 1. A stored `list` is now honoured. The old read only recognised `cards` and
 *    fell through to the fallback otherwise, so an explicit preference for the
 *    table was indistinguishable from never having chosen — which is exactly
 *    what a mobile-aware default would have overridden.
 * 2. With nothing stored, phones start in cards. A seven-column table on a
 *    375px screen is a horizontal scroll with three columns hidden; the card is
 *    the layout that actually fits, and the toggle is still there for anyone who
 *    wants the table.
 */
export function useCollectionView(storageKey: string, fallback?: CollectionView) {
  const [view, setViewState] = useState<CollectionView>(() => {
    if (typeof window === 'undefined') return fallback ?? 'list';
    const stored = window.localStorage.getItem(storageKey);
    if (stored === 'cards' || stored === 'list') return stored;
    if (fallback) return fallback;
    return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_QUERY).matches
      ? 'cards'
      : 'list';
  });

  const setView = (next: CollectionView) => {
    setViewState(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, next);
  };

  return [view, setView] as const;
}

const MOBILE_QUERY = '(max-width: 767px)';

export function CollectionViewToggle({
  view,
  onChange,
  listLabel = 'Table view',
}: {
  view: CollectionView;
  onChange: (view: CollectionView) => void;
  listLabel?: string;
}) {
  return (
    <div
      className="inline-flex h-9 items-center rounded-md border border-border/70 bg-muted/45 p-0.5"
      role="group"
      aria-label="Choose collection view"
    >
      <button
        type="button"
        aria-pressed={view === 'list'}
        aria-label={listLabel}
        onClick={() => onChange('list')}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-[5px] px-2.5 text-[12px] font-medium transition-colors',
          view === 'list'
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <Rows3 className="h-3.5 w-3.5" aria-hidden />
        <span className="hidden sm:inline">{listLabel}</span>
      </button>
      <button
        type="button"
        aria-pressed={view === 'cards'}
        aria-label="Card view"
        onClick={() => onChange('cards')}
        className={cn(
          'inline-flex h-8 items-center gap-1.5 rounded-[5px] px-2.5 text-[12px] font-medium transition-colors',
          view === 'cards'
            ? 'bg-card text-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
        <span className="hidden sm:inline">Card view</span>
      </button>
    </div>
  );
}

export function RecordCardGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">{children}</div>;
}

export interface RecordCardDetail {
  label: string;
  value?: ReactNode;
  /**
   * Give this detail the full card width instead of one grid column. For the
   * one fact that is a sentence rather than a value — a summary, a next step.
   */
  wide?: boolean;
}

/**
 * The standard record card.
 *
 * Reading order is deliberate and fixed, because a grid of cards is scanned
 * column-first: identity (title, subtitle) → state (badges, on the same
 * row as the title so status is legible before any detail is read) → the
 * headline fact → supporting details → quiet metadata → actions pinned to the
 * bottom edge so every card in a row ends with its buttons aligned.
 *
 * `details`, `badges`, `onOpen`, and `openLabel` are the original contract;
 * `headline`, `meta`, `actions`, and `flagged` are additive, so a caller that
 * passes none of them gets the same card it always did, only tightened.
 */
export function RecordCard({
  recordType,
  recordTypeDefinition,
  recordTypeIcon,
  title,
  subtitle,
  image,
  badges,
  headline,
  details,
  meta,
  actions,
  flagged = false,
  onOpen,
  openLabel = 'Open record',
}: {
  /**
   * Renders the identity strip along the card's top edge. Optional only so the
   * handful of pre-strip callers keep compiling; every record type should pass
   * it.
   */
  recordType?: RecordTypeId;
  /** App-specific registry identity; preferred over the legacy global id. */
  recordTypeDefinition?: RegisteredRecordTypeDefinition;
  recordTypeIcon?: LucideIcon;
  /** @deprecated Record identity belongs in the colored strip, not the body. */
  icon?: LucideIcon;
  title: string;
  subtitle?: ReactNode;
  /** Optional featured image displayed above the card identity. */
  image?: { src: string; alt: string };
  badges?: ReactNode;
  /** The one line that explains this record — a summary or next step. */
  headline?: ReactNode;
  details: RecordCardDetail[];
  /** Quiet footer facts: counts, timestamps, provenance. */
  meta?: ReactNode;
  /** Extra controls beside the Open button. */
  actions?: ReactNode;
  /** Draw attention to the card — a lead in the loop, an item needing triage. */
  flagged?: boolean;
  onOpen: () => void;
  openLabel?: string;
}) {
  const visible = details.filter((detail) => detail.value !== undefined && detail.value !== null && detail.value !== '');

  return (
    <Card
      className={cn(
        // Padding moves to the inner column so the identity strip can run edge
        // to edge along the card's top.
        'flex h-full min-h-[210px] flex-col overflow-hidden p-0 transition-all',
        'hover:-translate-y-px hover:border-primary/40 hover:shadow-md',
        flagged && 'border-primary bg-selection',
      )}
    >
      {recordTypeDefinition && recordTypeIcon ? (
        <RegisteredRecordTypeStrip definition={recordTypeDefinition} icon={recordTypeIcon} />
      ) : recordType ? (
        <RecordTypeStrip type={recordType} />
      ) : null}
      {image && (
        <button
          type="button"
          onClick={onOpen}
          className="block h-36 w-full overflow-hidden border-b border-border bg-muted/30 text-left"
          aria-label={openLabel}
        >
          <img
            src={image.src}
            alt={image.alt}
            className="h-full w-full object-cover transition-transform duration-200 hover:scale-[1.015]"
            loading="lazy"
          />
        </button>
      )}
      <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            className="line-clamp-2 rounded-sm text-left text-[15px] font-semibold leading-tight text-foreground underline-offset-2 hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {title}
          </button>
          {subtitle && (
            <div className="mt-1 truncate text-[12px] leading-tight text-muted-foreground">
              {subtitle}
            </div>
          )}
        </div>
        {badges && <div className="flex shrink-0 flex-wrap justify-end gap-1.5">{badges}</div>}
      </div>

      {headline && (
        <p className="line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">{headline}</p>
      )}

      {visible.length > 0 && (
        <dl className="grid gap-x-4 gap-y-2.5 text-[12px] sm:grid-cols-2">
          {visible.map((detail) => (
            <div key={detail.label} className={cn('min-w-0', detail.wide && 'sm:col-span-2')}>
              <dt className="text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                {detail.label}
              </dt>
              <dd className="mt-0.5 truncate text-[13px] leading-snug text-foreground">
                {detail.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {meta && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
          {meta}
        </div>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-border/70 pt-3">
        {actions}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={cn('gap-1.5', actions ? 'ml-auto' : 'w-full')}
          onClick={onOpen}
        >
          {openLabel}
        </Button>
      </div>
      </div>
    </Card>
  );
}

/**
 * Cascade's shared version of the One Up compact-list + center-detail workspace.
 * Below `xl` the shared MasterDetail shows one pane at a time so selecting a
 * record opens it instead of appending it under the full list.
 */
export function WorkspaceSplitView({
  children,
  detail,
  detailKey,
}: {
  children: ReactNode;
  detail?: ReactNode;
  /** Id of the open record, so the narrow-view scroll reset tracks selection. */
  detailKey?: string;
}) {
  return (
    <MasterDetail
      list={<div className="min-w-0">{children}</div>}
      detail={detail}
      detailKey={detailKey}
      splitAt="xl"
      listWidth="minmax(260px,340px)"
    />
  );
}

/**
 * True when the workspace is wide enough to show the list and the detail
 * together — i.e. when `WorkspaceSplitView` is NOT in one-pane mode.
 *
 * List pages need this to decide whether their own furniture still applies.
 * Below the split, the detail replaces the list, so a search box and a set of
 * filters sitting above it act on a list nobody can see: they occupy the top of
 * a phone screen and do nothing. Pages hide them when this returns false.
 */
export function useIsSplitView(splitAt: 'lg' | 'xl' | '2xl' = 'xl'): boolean {
  return useMediaQuery(SPLIT_VIEW_QUERIES[splitAt]);
}

const SPLIT_VIEW_QUERIES = {
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  '2xl': '(min-width: 1536px)',
} as const;

export function CompactRecordList({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {title}
      </div>
      <div className="p-1.5 xl:max-h-[calc(100vh-280px)] xl:overflow-y-auto">{children}</div>
    </div>
  );
}

export function CompactRecordListItem({
  selected,
  title,
  subtitle,
  meta,
  onSelect,
}: {
  selected?: boolean;
  title: string;
  subtitle?: ReactNode;
  meta?: ReactNode;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col rounded-md px-2.5 py-2 text-left transition-colors',
        selected ? 'bg-selection text-foreground' : 'text-foreground hover:bg-muted/70',
      )}
    >
      <span className="truncate text-[13px] font-medium">{title}</span>
      {subtitle && (
        <span className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</span>
      )}
      {meta && <span className="mt-1 text-[11px] text-muted-foreground">{meta}</span>}
    </button>
  );
}

/**
 * The record detail frame: identity strip, a thin navigation bar, then content.
 *
 * The navigation bar deliberately does NOT repeat the record's title. It used
 * to, which meant an open Lead showed its name three times in the first 120px —
 * here, in the RecordDetailHeading below, and again as the body's first field.
 * This bar's job is navigation (back, previous, next); the strip states the
 * type; the heading beneath owns the name. One title, one place.
 */
export function RecordDetailShell({
  recordType,
  recordTypeDefinition,
  recordTypeIcon,
  typeLabel,
  onClose,
  onPrevious,
  onNext,
  embedded = false,
  children,
}: {
  recordType?: RecordTypeId;
  /** App-specific registry identity; preferred over the legacy global id. */
  recordTypeDefinition?: RegisteredRecordTypeDefinition;
  recordTypeIcon?: LucideIcon;
  /** Overrides the registry label in aria text only. */
  typeLabel?: string;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  /** The persistent right sidebar owns identity/navigation chrome when embedded. */
  embedded?: boolean;
  children: ReactNode;
}) {
  const noun = typeLabel
    ?? recordTypeDefinition?.singularLabel.toLowerCase()
    ?? (recordType ? RECORD_TYPE_NOUN[recordType] : 'record');
  if (embedded) return <section className="min-w-0 overflow-hidden">{children}</section>;
  return (
    <section className="min-w-0 overflow-hidden rounded-lg border border-border bg-card">
      {recordTypeDefinition && recordTypeIcon ? (
        <RegisteredRecordTypeStrip definition={recordTypeDefinition} icon={recordTypeIcon} />
      ) : recordType ? (
        <RecordTypeStrip type={recordType} />
      ) : null}
      <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
        {/*
          Below the split breakpoint the detail replaces the list, so the
          dismiss control needs to read as "back to the list" rather than a bare
          close glyph.
        */}
        <button
          type="button"
          onClick={onClose}
          className="flex flex-shrink-0 items-center gap-1 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Close ${noun} detail`}
        >
          <ArrowLeft className="h-4 w-4 xl:hidden" aria-hidden />
          <span className="text-[12px] xl:hidden">Back</span>
          <X className="hidden h-4 w-4 xl:block" aria-hidden />
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onPrevious}
          disabled={!onPrevious}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
          aria-label={`Previous ${noun}`}
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!onNext}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
          aria-label={`Next ${noun}`}
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {children}
    </section>
  );
}

const RECORD_TYPE_NOUN: Record<RecordTypeId, string> = {
  lead: 'lead',
  form_entry: 'form entry',
  call: 'call',
  contact: 'contact',
  organization: 'organization',
  form: 'form',
  agent: 'agent',
  email: 'email',
  payment: 'payment',
  time_entry: 'Time Entry',
  task: 'task',
  project: 'project',
  website: 'website',
};

export function RecordDetailHeading({
  icon: Icon,
  media,
  title,
  badges,
  actions,
}: {
  icon?: LucideIcon;
  media?: ReactNode;
  title: string;
  badges?: ReactNode;
  /**
   * Primary record actions, aligned opposite the title. Belongs here rather
   * than buried at the bottom of a tab body: acting on a record should not
   * require scrolling past its details first.
   */
  actions?: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 border-b border-border px-4 py-3 sm:flex sm:flex-wrap">
      {media ?? (Icon && (
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emphasis text-emphasis-foreground">
          <Icon className="h-5 w-5" aria-hidden />
        </span>
      ))}
      <div className="min-w-0 flex-1">
        <h2 className="break-words text-[15px] font-semibold leading-tight text-foreground">
          {title}
        </h2>
        {badges && <div className="mt-1 flex flex-wrap items-center gap-2">{badges}</div>}
      </div>
      {actions && (
        <div className="col-span-2 flex w-full flex-wrap items-center justify-start gap-2 sm:ml-auto sm:w-auto sm:shrink-0 sm:justify-end">
          {actions}
        </div>
      )}
    </div>
  );
}

export interface RecordTab {
  id: string;
  label: string;
  count?: number;
}

export function RecordTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: RecordTab[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto border-b border-border px-3" role="tablist">
      <div className="flex min-w-max gap-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active === tab.id}
            onClick={() => onChange(tab.id)}
            className={cn(
              'border-b-2 px-2.5 py-2 text-[12px] font-medium transition-colors',
              active === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-border px-4 py-4 last:border-b-0">
      <h3 className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}
